import { useEffect, useState } from 'react';
import { useReturnFocus } from './Dialog';
import * as RadixDialog from '@radix-ui/react-dialog';
import { messagePlainText, type LocalMessage } from '../data/messages';
import { PRIMARY_REACTIONS } from '../data/emoji';
import { EmojiChooser } from './ReactionBar';
import { Icon, type IconName } from './Icon';
import type { MessageActions } from './MessageRow';
import { useBackToClose } from '../lib/back-to-close';

/**
 * Uzenet-muveletek MOBILON, egy also lapon.
 *
 * Asztalon a lebego sav (lasd `HoverActions`) jo megoldas: a muveletek csak
 * akkor latszanak, amikor az eger az uzeneten van, tehat nem zajositjak a
 * beszelgetest. Erintessel viszont NINCS hover - ott ugyanezek a muveletek
 * gyakorlatilag elerhetetlenek voltak.
 *
 * Ezert erintokepernyon a hosszu nyomas hozza elo oket, EGYUTT: itt nincs
 * "gyakori" es "ritkabb" szetvalasztas, mert a lapon fer mind. Amit az ember
 * keres, azt egy listaban talalja meg, nem egy sav es egy legordulo menu
 * kozott megosztva.
 *
 * A lap ALULROL jon: telefonon a hivelyk ott er el, a kepernyo teteje nem.
 */
export function MessageSheet({
  open,
  onOpenChange,
  message,
  isMine,
  canManage,
  actions,
  onReact,
  onReply,
  onEdit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: LocalMessage;
  isMine: boolean;
  canManage: boolean;
  actions: MessageActions;
  onReact: (emoji: string) => void;
  onReply: () => void;
  onEdit: () => void;
}) {
  useBackToClose(open, () => onOpenChange(false));
  /**
   * Ket allapot EGY lapon belul.
   *
   * A teljes emoji-keszlet nem fer a muveletek melle, egy popover pedig egy
   * mar nyitva levo lap folott a kepernyo felet elfedne. Igy a lap tartalma
   * valt: a "További reakciók" a valasztora cserelodik, es a Vissza hozza
   * ujra a muveleteket.
   */
  const [view, setView] = useState<'menu' | 'emoji'>('menu');

  // Ujranyitaskor mindig a muveletek jonnek - a kereses nem "ragad be".
  useEffect(() => {
    if (open) setView('menu');
  }, [open]);

  const run = (action: () => void) => {
    onOpenChange(false);
    action();
  };

  const preview = messagePlainText(message);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  // Bezaraskor a fokusz vissza a nyito elemre (lasd Dialog.tsx).
  const returnFocus = useReturnFocus(open);
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <RadixDialog.Content
          onCloseAutoFocus={returnFocus}
          className={[
            'animate-sheet-up fixed inset-x-0 bottom-0 z-50 flex max-h-[calc(var(--app-height,100dvh)*0.85)] flex-col',
            'rounded-t-card border-t border-border-soft bg-surface-raised text-ink',
            'pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-(--shadow-modal)',
          ].join(' ')}
        >
          <RadixDialog.Title className="sr-only">Üzenet műveletei</RadixDialog.Title>
          <RadixDialog.Description className="sr-only">
            {preview || 'Melléklet'}
          </RadixDialog.Description>

          {/* Fogantyu: ettol latszik, hogy ez egy alulrol felhuzott lap. */}
          <div aria-hidden="true" className="flex shrink-0 justify-center pb-1 pt-2">
            <span className="h-1 w-10 rounded-pill bg-border-strong" />
          </div>

          {/*
            MELYIK uzenetrol van szo.

            Hosszu nyomasnal konnyu melle nyulni, es a lap elfedi a folyam
            aljat. Egy sornyi reszlet eleg ahhoz, hogy az ember lassa: tenyleg
            azt torli vagy tuzi ki, amit akart.
          */}
          {preview && (
            <p className="shrink-0 truncate border-b border-border-soft px-4 pb-2.5 text-xs text-ink-faint">
              {preview}
            </p>
          )}

          {view === 'emoji' ? (
            <div className="min-h-0 flex-1 overflow-hidden px-3 pt-3">
              <button
                type="button"
                onClick={() => setView('menu')}
                className="mb-2 flex items-center gap-2 rounded-control px-1 py-1 text-sm text-ink-muted"
              >
                <Icon name="balra" size="sm" /> Vissza
              </button>
              <EmojiChooser
                height="max-h-[calc(var(--app-height,100dvh)*0.45)]"
                onPick={(emoji) => run(() => onReact(emoji))}
              />
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {/*
                REAKCIOK elol, nagy gombokkal.

                Ez a leggyakoribb valasz egy uzenetre, es erintessel a
                huszonnyolc keppontos ikonokba nem lehet beletalalni - itt
                mindegyik gomb tenyleges ujjnyi.
              */}
              <div className="flex items-center gap-2 px-3 py-3">
                {PRIMARY_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    aria-label={`Reakció: ${emoji}`}
                    onClick={() => run(() => onReact(emoji))}
                    className="grid size-12 place-items-center rounded-control border border-border-soft bg-surface text-2xl active:bg-surface-hover"
                  >
                    {emoji}
                  </button>
                ))}

                <button
                  type="button"
                  aria-label="További reakciók"
                  onClick={() => setView('emoji')}
                  className="grid size-12 place-items-center rounded-control border border-border-soft bg-surface text-ink-muted active:bg-surface-hover"
                >
                  <Icon name="emoji" />
                </button>
              </div>

              <div className="border-t border-border-soft pt-1">
                <SheetItem icon="valasz" onClick={() => run(onReply)}>
                  Válasz
                </SheetItem>

                {preview !== '' && (
                  <SheetItem icon="masolas" onClick={() => run(() => actions.onCopyText(message))}>
                    Szöveg másolása
                  </SheetItem>
                )}

                {actions.onToggleTask && (
                  <SheetItem
                    icon="pipa"
                    onClick={() => run(() => actions.onToggleTask?.(message.id, !message.task))}
                  >
                    {message.task ? 'Feladat-jelölés levétele' : 'Megjelölés feladatként'}
                  </SheetItem>
                )}

                <SheetItem
                  icon="figyelmeztetes"
                  onClick={() =>
                    run(() => actions.onToggleImportant(message.id, !message.isImportant))
                  }
                >
                  {message.isImportant ? 'Fontos jelölés levétele' : 'Megjelölés fontosként'}
                </SheetItem>

                <SheetItem
                  icon="kituzes"
                  onClick={() => run(() => actions.onTogglePin(message.id))}
                >
                  {message.isPinned ? 'Kitűzés levétele' : 'Kitűzés a csatornában'}
                </SheetItem>

                <SheetItem
                  icon="mentes"
                  onClick={() => run(() => actions.onToggleSaved(message.id))}
                >
                  {message.isSaved ? 'Mentés visszavonása' : 'Mentés későbbre'}
                </SheetItem>

                <SheetItem
                  icon="megnyitas"
                  onClick={() => run(() => actions.onCopyLink(message.id))}
                >
                  Link másolása
                </SheetItem>

                {/*
                  A TELEFON SAJAT megosztasa (K2): e-mail, Viber, jegyzet - amit
                  a telefon kinal. A link csak annak nyilik meg, aki latja a
                  csatornat; a szoveg viszont maga az uzenet.
                */}
                {canShare && (
                  <SheetItem
                    icon="megosztas"
                    onClick={() =>
                      run(() => {
                        void navigator
                          .share({
                            text: preview || undefined,
                            url: `${window.location.origin}/c/${message.channelId}?m=${message.id}`,
                          })
                          .catch(() => undefined);
                      })
                    }
                  >
                    Megosztás…
                  </SheetItem>
                )}

                <SheetItem icon="tovabbitas" onClick={() => run(() => actions.onForward(message))}>
                  Továbbítás
                </SheetItem>

                {actions.onSelectForForward && (
                  <SheetItem
                    icon="tovabbitas"
                    onClick={() => run(() => actions.onSelectForForward?.(message))}
                  >
                    Több üzenet továbbítása…
                  </SheetItem>
                )}

                {actions.onMarkUnread && !message.pending && message.seq > 0 && (
                  <SheetItem icon="rejt" onClick={() => run(() => actions.onMarkUnread?.(message))}>
                    Olvasatlanná jelölés innen
                  </SheetItem>
                )}

                {/* A cegnel kikapcsolt integracio nem szurke sor, hanem nincs ott. */}
                {actions.onProjectflow && (
                  <SheetItem
                    icon="pipa"
                    onClick={() => run(() => actions.onProjectflow?.(message))}
                  >
                    Küldés a ProjectFlow-ba
                  </SheetItem>
                )}

                {(isMine || canManage) && <div className="my-1 h-px bg-border-soft" />}

                {isMine && !message.failed && (
                  <SheetItem icon="kuldes" onClick={() => run(() => actions.onResend(message))}>
                    Újraküldés
                  </SheetItem>
                )}

                {isMine && (
                  <SheetItem icon="szerkesztes" onClick={() => run(onEdit)}>
                    Szerkesztés
                  </SheetItem>
                )}

                {(isMine || canManage) && (
                  <SheetItem
                    danger
                    icon="torles"
                    onClick={() => run(() => actions.onDelete(message.id))}
                  >
                    Törlés
                  </SheetItem>
                )}
              </div>
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

function SheetItem({
  icon,
  danger,
  onClick,
  children,
}: {
  icon: IconName;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        // A 44 keppont koruli magassag nem esztetikai dontes: ennel kisebb
        // celt ujjal nem lehet megbizhatoan eltalalni.
        'flex w-full items-center gap-3.5 px-4 py-3 text-left text-sm',
        danger ? 'text-danger active:bg-danger-soft' : 'active:bg-surface-hover',
      ].join(' ')}
    >
      <span aria-hidden="true" className={danger ? '' : 'text-ink-faint'}>
        <Icon name={icon} />
      </span>
      {children}
    </button>
  );
}
